import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/utils.helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Pencil, FileText, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch (e) {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = employees.filter(
    (e) =>
      !q ||
      e.name?.toLowerCase().includes(q.toLowerCase()) ||
      e.mobile?.includes(q) ||
      e.employee_code?.toLowerCase().includes(q.toLowerCase()) ||
      e.father_name?.toLowerCase().includes(q.toLowerCase()) ||
      e.department?.toLowerCase().includes(q.toLowerCase())
  );

  const del = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Workforce</div>
          <h1 className="font-serif text-4xl text-slate-900 mt-1">Employees</h1>
          <p className="text-slate-600 mt-1">Manage worker profiles, KYC & bank details.</p>
        </div>
        <Button onClick={() => nav("/admin/employees/new")} data-testid="add-employee-button" className="bg-[#111111] hover:bg-[#C59B27] text-white transition-colors duration-200">
          <UserPlus className="w-4 h-4 mr-2" /> Add Employee
        </Button>
      </div>

      <Card className="border-[#E5E2DC] p-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="employees-search-input"
            placeholder="Search by code, name, mobile, father's name or department..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
      </Card>

      <Card className="border-[#E5E2DC] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FDFDF9]">
              <TableHead className="uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Employee</TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Father's Name</TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Mobile</TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Joining</TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Wage</TableHead>
              <TableHead className="uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Status</TableHead>
              <TableHead className="text-right uppercase text-[10px] tracking-[0.1em] text-slate-500 font-bold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400" data-testid="empty-employees">No employees yet. Click "Add Employee" to create one.</TableCell></TableRow>
            ) : (
              filtered.map((e) => (
                <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`employee-row-${e.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {e.photo_url ? (
                        <img src={`${BACKEND_URL}${e.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover bg-slate-100" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#C59B27]/20 text-[#C59B27] flex items-center justify-center font-semibold text-sm">
                          {e.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">{e.name}</div>
                        <div className="text-xs text-slate-500">{e.employee_code ? `${e.employee_code} · ` : ""}{e.department || "—"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{e.father_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{e.mobile || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-700">{formatDate(e.joining_date)}</TableCell>
                  <TableCell className="text-sm text-slate-700">
                    {e.salary_type === "daily"
                      ? `${formatINR(e.daily_wage)}/day`
                      : `${formatINR(e.monthly_salary)}/mo`}
                  </TableCell>
                  <TableCell>
                    <Badge variant={e.status === "active" ? "default" : "secondary"} className={e.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-slate-100 text-slate-600"}>
                      {e.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => nav(`/admin/employees/${e.id}/slip`)} data-testid={`slip-${e.id}`} title="Salary Slip">
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => nav(`/admin/employees/${e.id}/edit`)} data-testid={`edit-${e.id}`} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" data-testid={`delete-${e.id}`} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this employee?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove <b>{e.name}</b> and their attendance & transaction history will remain but be orphaned.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(e.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
